/**
 * Typed errors raised by `useCorrespondenceQuery` and
 * `useCorrespondenceMutation`. Callers catch `CorrespondenceApiError` for
 * generic handling and narrow via `instanceof UnsupportedOnProviderError`
 * for the one case (Outlook → Gmail-only action) that needs a distinct UX.
 *
 * The class mirrors the server's error envelope, plus `httpStatus`. The
 * status is preserved so callers can branch on transport-level semantics
 * (429 → backoff, 422 → UnsupportedOnProviderError) without re-reading
 * the Response object.
 *
 * Same shape as the other ad-hoc error classes in the frontend
 * (`ThreadIdRejectedError`, `MissingProviderError`): `extends Error`,
 * sets `this.name`, exposes readonly domain fields.
 */

import type { CorrespondenceAction, Provider } from '@/lib/correspondence/contracts';

/**
 * Bounded set of error codes the facade emits. Declared as a string-literal
 * union mirroring `CorrespondenceErrorCode` from
 * `_shared/email/adapters/errors.ts`. Kept separate on the frontend because
 * the edge-function file lives behind URL-specifier imports and can't be
 * re-exported via the contracts bridge.
 *
 * Parity with the backend enum is enforced against the shared fixture
 * `_shared/email/__fixtures__/contracts/reason-parity.fixtures.ts`, asserted
 * from both runtimes (`src/lib/correspondence/contracts.test.ts` and
 * `_shared/email/contracts.cross-runtime.test.ts`). A member added to one
 * side and not the other fails to compile.
 */
export type CorrespondenceErrorCode =
  | 'UNSUPPORTED_ON_PROVIDER'
  | 'VALIDATION_FAILED'
  | 'ACCOUNT_NOT_FOUND'
  | 'ACCOUNT_NOT_CONNECTED'
  | 'PERMISSION_DENIED'
  | 'TOKEN_REVOKED'
  | 'PROVIDER_THROTTLED'
  | 'PROVIDER_OUTAGE'
  | 'CONFLICT'
  | 'NOT_FOUND'
  | 'PARTIAL_BATCH_FAILURE'
  | 'MISSING_PROVIDER'
  | 'THREAD_ID_REJECTED'
  | 'INTERNAL_ERROR';

export interface CorrespondenceApiErrorInit {
  readonly message: string;
  readonly code: CorrespondenceErrorCode;
  readonly httpStatus: number;
  readonly provider?: Provider;
  readonly action?: CorrespondenceAction;
  readonly details?: Record<string, unknown>;
  readonly cause?: unknown;
}

export class CorrespondenceApiError extends Error {
  readonly code: CorrespondenceErrorCode;
  readonly httpStatus: number;
  readonly provider?: Provider;
  readonly action?: CorrespondenceAction;
  // `details` is explicitly declared NON-ENUMERABLE below (see the
  // `Object.defineProperty` call). Rationale: `@sentry/react` (and most
  // structured loggers) serialise enumerable own properties on thrown
  // Error instances. `details.rawBody` is a last-resort debugging hatch
  // populated only when the facade envelope parse fails, and the raw
  // upstream body may carry PII (e.g. a CDN/WAF/Vault failure that
  // reflects request fragments). Hiding the property from enumeration
  // keeps Sentry/console auto-serializers from capturing PII. Callers
  // that explicitly read `error.details` still work — the property is
  // accessible, just not enumerable.
  readonly details?: Record<string, unknown>;
  override readonly cause?: unknown;

  constructor(init: CorrespondenceApiErrorInit) {
    super(init.message);
    this.name = 'CorrespondenceApiError';
    this.code = init.code;
    this.httpStatus = init.httpStatus;
    this.provider = init.provider;
    this.action = init.action;
    this.cause = init.cause;
    // Non-enumerable `details` — see class-level comment for rationale.
    Object.defineProperty(this, 'details', {
      value: init.details,
      enumerable: false,
      writable: false,
      configurable: false,
    });
  }
}

/**
 * Subclass raised when the facade returns HTTP 422 + `UNSUPPORTED_ON_PROVIDER`.
 * Thrown in the hook's queryFn/mutationFn so callers can pattern-match via
 * `instanceof UnsupportedOnProviderError` without string-comparing `.code`.
 *
 * `provider` and `action` are REQUIRED — the facade always populates both on
 * this code path, and the UI needs them to produce the "not available on
 * {provider}" copy.
 */
export class UnsupportedOnProviderError extends CorrespondenceApiError {
  // `declare` narrows the inherited field types without re-declaring runtime
  // properties — the parent constructor sets the actual values via
  // `super({code: 'UNSUPPORTED_ON_PROVIDER', provider, action, …})`.
  declare readonly code: 'UNSUPPORTED_ON_PROVIDER';
  declare readonly provider: Provider;
  declare readonly action: CorrespondenceAction;

  constructor(provider: Provider, action: CorrespondenceAction, details?: Record<string, unknown>) {
    super({
      message: `Action ${action} is not supported on ${provider}`,
      code: 'UNSUPPORTED_ON_PROVIDER',
      httpStatus: 422,
      provider,
      action,
      details,
    });
    this.name = 'UnsupportedOnProviderError';
  }
}

/**
 * Bounded reason codes for `UnauthorizedAliasError`. Mirror the strings the
 * backend mapper sets in `details.reason` for `PERMISSION_DENIED` on
 * `send_email` (see `_shared/email/adapters/sender-validation-mapper.ts`).
 */
export type UnauthorizedAliasReason = 'sender_not_authorized' | 'sender_not_verified';

/**
 * Subclass raised when a `send_email` is rejected because the requested
 * `fromAlias` is not an authorised send-as for the resolved account.
 * Thrown by `mapSendError` (`use-email-send/facade-send.ts`) on facade
 * `PERMISSION_DENIED` for `send_email` so the inline-compose UI can
 * surface a typed, actionable toast ("Choose a different From address")
 * instead of the generic destructive copy.
 *
 * Two reason codes:
 * - `sender_not_authorized` — the alias isn't configured for this account.
 * - `sender_not_verified`   — the alias exists but isn't verified.
 *
 * MUST NOT carry the requested email or any constituent-derived value on
 * the error message — see `.claude/rules/security.md` "Privacy in Logs"
 * and the corresponding boundary anchor on
 * `_shared/email/adapters/sender-validation-mapper.ts`.
 */
export class UnauthorizedAliasError extends CorrespondenceApiError {
  declare readonly code: 'PERMISSION_DENIED';
  readonly reason: UnauthorizedAliasReason;

  constructor(reason: UnauthorizedAliasReason, init: Omit<CorrespondenceApiErrorInit, 'code'>) {
    super({ ...init, code: 'PERMISSION_DENIED' });
    this.name = 'UnauthorizedAliasError';
    this.reason = reason;
  }
}

/**
 * Bounded reasons for `PERMISSION_DENIED` errors thrown by the
 * write-authorization layer (`_shared/email/adapters/write-auth.ts`) and by
 * provider helpers when a provider itself refuses an action
 * (`_shared/email/adapters/gmail-helpers.ts`,
 * `_shared/email/adapters/outlook-helpers.ts`). Mirrors `PermissionDeniedReason`
 * in `_shared/email/adapters/errors.ts`. Used by `getPermissionDeniedReason`
 * and `ScopeRevokedError` so callers can branch on cause without
 * string-comparing free-form text.
 *
 * Adding a value: extend the backend type in the same PR and add toast copy.
 * Unknown values from older sessions or future deploys default to
 * `provider_refused_other` via `getPermissionDeniedReason` — that's the
 * partial-deploy safety net.
 */
export type PermissionDeniedReason =
  | 'shared_needs_approver'
  | 'shared_needs_membership'
  | 'personal_owner_mismatch'
  | 'provider_refused_scope'
  | 'provider_refused_other'
  | 'on_behalf_not_authorized'
  | 'personal_needs_share'
  | 'correspondence_span_too_wide';

/**
 * Builds a type predicate from an exhaustive membership record.
 *
 * The record — not an array of literals — is what makes the set exhaustive:
 * `Record<Union, true>` fails to compile when a member is omitted, whereas
 * an array annotated `readonly Union[]` only proves each entry IS a member
 * and stays valid when one is missing. That difference is how a reason ends
 * up silently degrading instead of being branched on.
 *
 * Returning a predicate also keeps the narrowing honest — callers get
 * `value is Union` from a runtime check rather than an unchecked cast.
 */
function reasonGuard<T extends string>(members: Record<T, true>): (value: unknown) => value is T {
  const known = new Set<string>(Object.keys(members));
  return (value: unknown): value is T => typeof value === 'string' && known.has(value);
}

const isPermissionDeniedReason = reasonGuard<PermissionDeniedReason>({
  shared_needs_approver: true,
  shared_needs_membership: true,
  personal_owner_mismatch: true,
  provider_refused_scope: true,
  provider_refused_other: true,
  on_behalf_not_authorized: true,
  personal_needs_share: true,
  correspondence_span_too_wide: true,
});

/**
 * Returns the typed `PermissionDeniedReason` an error carries, defaulting
 * unknown values to `'provider_refused_other'` so partial-deploy windows
 * (frontend ahead of backend, or vice versa) degrade gracefully rather
 * than dropping toasts. Returns `null` if the error is not
 * `PERMISSION_DENIED` so callers can short-circuit.
 *
 * Mirrors the backend's `details.reason` enum surface.
 */
export function getPermissionDeniedReason(
  error: CorrespondenceApiError,
): PermissionDeniedReason | null {
  if (error.code !== 'PERMISSION_DENIED') {
    return null;
  }
  const raw = error.details?.reason;
  return isPermissionDeniedReason(raw) ? raw : 'provider_refused_other';
}

/**
 * Mirrors `ValidationFailedReason` in `_shared/email/adapters/errors.ts`.
 * `on_behalf_attempt_invalid` means the on-behalf attempt's identifiers or
 * canonical payload failed the create RPC's own validation — the request is
 * malformed, so retrying it unchanged cannot succeed. `invalid_continuation`
 * means a list page cursor was rejected as not continuing the requested list.
 */
export type ValidationFailedReason = 'on_behalf_attempt_invalid' | 'invalid_continuation';

const isValidationFailedReason = reasonGuard<ValidationFailedReason>({
  on_behalf_attempt_invalid: true,
  invalid_continuation: true,
});

/**
 * Mirrors `ConflictReason` in `_shared/email/adapters/errors.ts`.
 *
 * `on_behalf_attempt_not_claimable` is the one the UI must branch on: the
 * attempt moved past `authorized`, so a delivery may already have happened.
 * Offering a retry on that branch risks sending the same reply twice.
 * `on_behalf_attempt_conflict` means a replayed attempt id arrived with
 * different field values — a caller bug, not a delivery ambiguity.
 * `representation_not_dispatched` refuses a constituent confirmation whose
 * representation is unsent; nothing was dispatched, so it is safe to retry
 * once the representation goes out.
 */
export type ConflictReason =
  | 'on_behalf_attempt_conflict'
  | 'minister_send_mismatch'
  | 'on_behalf_attempt_not_claimable'
  | 'representation_not_dispatched'
  | 'send_delivery_unconfirmed';

const isConflictReason = reasonGuard<ConflictReason>({
  send_delivery_unconfirmed: true,
  on_behalf_attempt_conflict: true,
  on_behalf_attempt_not_claimable: true,
  representation_not_dispatched: true,
  minister_send_mismatch: true,
});

/**
 * Returns the typed `ConflictReason` a `CONFLICT` error carries, or `null`
 * when the code differs or the reason is one this build does not know.
 *
 * Unlike `getPermissionDeniedReason` there is no safe default: every
 * conflict reason implies a different recovery, and guessing between
 * "your input was stale" and "this may already have been delivered" is
 * exactly the mistake that would double-send. Callers MUST treat `null` as
 * "unknown conflict — do not retry automatically".
 */
export function getConflictReason(error: CorrespondenceApiError): ConflictReason | null {
  if (error.code !== 'CONFLICT') {
    return null;
  }
  const raw = error.details?.reason;
  return isConflictReason(raw) ? raw : null;
}

/**
 * Returns the typed `ValidationFailedReason` a `VALIDATION_FAILED` error
 * carries, or `null` when the code differs or the reason is unknown to this
 * build. Most validation failures carry zod issues rather than a bounded
 * reason, so `null` is the ordinary case, not an error.
 */
export function getValidationFailedReason(
  error: CorrespondenceApiError,
): ValidationFailedReason | null {
  if (error.code !== 'VALIDATION_FAILED') {
    return null;
  }
  const raw = error.details?.reason;
  return isValidationFailedReason(raw) ? raw : null;
}

/**
 * Three send failures with DIFFERENT certainty about the outcome. The first two
 * are DEFINITE — the server refused before transmitting anything, so a
 * shrink-and-retry is safe — and they differ only in what was measured, which
 * is what makes their remedies different:
 *
 * - `attachment_too_large` — the backend send guard refused the payload up
 *   front with a typed `VALIDATION_FAILED` + `attachment_size_exceeded`. A
 *   clean, definite outcome: nothing was sent, resending after shrinking the
 *   attachments is safe. Measured on the DECLARED attachment bytes, before any
 *   MIME is assembled, so only the attachments are on the scale.
 * - `message_too_large` — `VALIDATION_FAILED` + `message_size_exceeded`, raised
 *   against the ASSEMBLED RFC 822 message (base64 expansion, the body, the
 *   signature, the quoted history and the MIME framing all counted). Equally
 *   definite, but shrinking attachments alone need not clear it — the copy must
 *   offer shortening the message as well. It is also reachable with ZERO picked
 *   attachments: signature and inline images only become `attachments[]` at
 *   `use-email-send/facade-send.ts::buildFacadeAttachments`, downstream of every
 *   client guard, so the copy must not read as an instruction to remove
 *   attachments the user may not have.
 * - `send_interrupted` — the send returned a worker-kill signature: HTTP 546,
 *   or a non-envelope body on a 5xx / unknown (0) status (a non-envelope 4xx
 *   is a transport/auth refusal, not this). The worker died mid-request
 *   (observed prod cause: the isolate exceeding its memory limit while
 *   encoding a large message — see the branch's solution doc), so the outcome
 *   is UNKNOWN: the provider may or may not have accepted the message before
 *   the kill. The copy must never assert "not sent" — it asks the user to
 *   check the conversation before retrying, or a blind resend can duplicate
 *   real correspondence.
 */
export type SendPayloadFailureKind =
  | 'attachment_too_large'
  | 'message_too_large'
  | 'send_interrupted';

/**
 * Classifies a send failure into the payload-size family, or `null` when it is
 * some other error. Works off the wire-level signals so both the definite
 * (typed 400) and the opaque (546 / non-envelope 5xx) cases route to actionable
 * copy — a raw "unrecognised error envelope (HTTP 546)" string is never shown.
 *
 * Consumed by `mapSendError` (which wraps the result in `SendPayloadError`) so
 * every send surface — inline compose and approve-and-send — renders the same
 * guidance.
 */
export function getSendPayloadFailureKind(error: unknown): SendPayloadFailureKind | null {
  if (!(error instanceof CorrespondenceApiError)) {
    return null;
  }
  if (error.code === 'VALIDATION_FAILED' && error.details?.reason === 'attachment_size_exceeded') {
    return 'attachment_too_large';
  }
  if (error.code === 'VALIDATION_FAILED' && error.details?.reason === 'message_size_exceeded') {
    return 'message_too_large';
  }
  // Only a worker-kill signature counts as an interrupted send: HTTP 546, or a
  // non-envelope body on a 5xx / unknown (0) status. A non-envelope 4xx is NOT
  // one — a WAF or auth layer answering 403 with an opaque body would otherwise
  // get "check the conversation / reduce attachment size" guidance for a
  // failure that has nothing to do with either.
  if (error.httpStatus === 546) {
    return 'send_interrupted';
  }
  if (
    error.details?.unrecognizedEnvelope === true &&
    (error.httpStatus === 0 || error.httpStatus >= 500)
  ) {
    return 'send_interrupted';
  }
  return null;
}

/**
 * Subclass raised by `mapSendError` for the payload-size family above. Preserves
 * the underlying `code`/`httpStatus`/`details` (so `getSendPayloadFailureKind`
 * still classifies it) while carrying a friendly, translated `message` for
 * surfaces that render `error.message` directly, plus a `kind` discriminant for
 * surfaces that pick their own title + description copy.
 */
export class SendPayloadError extends CorrespondenceApiError {
  readonly kind: SendPayloadFailureKind;

  constructor(kind: SendPayloadFailureKind, init: CorrespondenceApiErrorInit) {
    super(init);
    this.name = 'SendPayloadError';
    this.kind = kind;
  }
}

/**
 * Subclass raised when a provider has revoked our access scope (Gmail
 * `ACCESS_TOKEN_SCOPE_INSUFFICIENT` etc.). Distinct from
 * `UnauthorizedAliasError` (alias-level) and the generic
 * `CorrespondenceApiError` (everything else): the UI gates on
 * `instanceof ScopeRevokedError` to render the reconnect-account CTA
 * instead of a generic toast, since the user must re-grant OAuth scope
 * before any subsequent action will succeed.
 *
 * The backend pairs this with an atomic `connection_status='disconnected'`
 * flip on `email_accounts`, so the existing `<AccountDisconnectedState />`
 * also renders on the next mount.
 */
export class ScopeRevokedError extends CorrespondenceApiError {
  declare readonly code: 'PERMISSION_DENIED';

  constructor(init: Omit<CorrespondenceApiErrorInit, 'code'>) {
    super({ ...init, code: 'PERMISSION_DENIED' });
    this.name = 'ScopeRevokedError';
  }
}

/**
 * Type guard for the scope-revoke branch. Use over a bare `instanceof` when
 * the caller may receive any subclass of `CorrespondenceApiError` and only
 * needs to gate the reconnect CTA on the scope-specific case.
 */
export function isScopeRevokedError(error: unknown): error is ScopeRevokedError {
  return error instanceof ScopeRevokedError;
}

/**
 * Subclass raised when Outlook's two-step `send_email` (draft create →
 * send) fails on step 2 AND the best-effort orphan-draft cleanup also
 * fails. The user's draft remains in their Outlook /Drafts folder and
 * may sync across devices, so the inline-compose UI surfaces a typed
 * toast ("Send failed; check your Outlook Drafts folder for the unsent
 * message") instead of the generic destructive copy. The single-step
 * `/me/sendMail` flow used to be the default but yielded no message id —
 * the two-step flow trades that round-trip for a non-null `messageId`,
 * with this typed warning as the safety net for the rare double-failure.
 */
export class OrphanedDraftWarning extends CorrespondenceApiError {
  declare readonly code: 'PROVIDER_OUTAGE';

  constructor(init: Omit<CorrespondenceApiErrorInit, 'code'>) {
    super({ ...init, code: 'PROVIDER_OUTAGE' });
    this.name = 'OrphanedDraftWarning';
  }
}
